import React, { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';

import { useBalanceUsage, usePlanConfig, useUpdateBalanceUsage, useUpdatePlanConfig } from '../../hooks/api';
import { Firm, UpdateBalanceUsageRequest, UpdatePlanConfigRequest } from '../../types/firm';
import { LoadingSpinner } from '../loading-spinner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { BalanceUsageForm } from './balance-usage-form';
import { PlanConfigDisplay } from './plan-config-display';
import { PlanConfigForm } from './plan-config-form';

type FirmDetailDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  firm: Firm | null;
};

export function FirmDetailDrawer({ isOpen, onClose, firm }: FirmDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState('plan-config');
  const [isEditingPlanConfig, setIsEditingPlanConfig] = useState(false);
  const [isEditingBalanceUsage, setIsEditingBalanceUsage] = useState(false);
  const [planConfigFormData, setPlanConfigFormData] = useState<UpdatePlanConfigRequest>({});
  const [planConfigSuccessMessage, setPlanConfigSuccessMessage] = useState<string | null>(null);
  const [balanceUsageSuccessMessage, setBalanceUsageSuccessMessage] = useState<string | null>(null);

  // Fetch plan config
  const {
    data: planConfigData,
    isLoading: isLoadingPlanConfig,
    error: planConfigError,
  } = usePlanConfig(firm?.id || null);

  // Fetch balance usage
  const {
    data: balanceUsageData,
    isLoading: isLoadingBalanceUsage,
    error: balanceUsageError,
  } = useBalanceUsage(firm?.id || null);
  console.log('balanceUsageData', balanceUsageData);
  const updatePlanConfig = useUpdatePlanConfig();
  const updateBalanceUsage = useUpdateBalanceUsage();

  // Initialize plan config form data when plan config loads
  useEffect(() => {
    if (planConfigData?.planConfig) {
      const config = planConfigData.planConfig;
      setPlanConfigFormData({
        plan: config.plan,
        deal_syndication: config.deal_syndication as any,
        data_room: config.data_room as any,
        ai: config.ai as any,
        lp_management: config.lp_management as any,
        team_collaboration: config.team_collaboration as any,
        compliance: config.compliance as any,
        cqdb: config.cqdb as any,
        seats: config.seats as any,
      });
    }
  }, [planConfigData]);

  if (!firm) return null;

  const getPlanBadgeVariant = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'secondary';
      case 'full':
        return 'success';
      case 'custom':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const handlePlanConfigSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firm?.id) return;

    setPlanConfigSuccessMessage(null);
    try {
      await updatePlanConfig.mutateAsync({
        firmId: firm.id,
        data: planConfigFormData,
      });
      setPlanConfigSuccessMessage('Plan configuration updated successfully!');
      setIsEditingPlanConfig(false);
      setTimeout(() => setPlanConfigSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update plan config:', error);
    }
  };

  const handlePlanConfigCancel = () => {
    if (planConfigData?.planConfig) {
      const config = planConfigData.planConfig;
      setPlanConfigFormData({
        plan: config.plan,
        deal_syndication: config.deal_syndication as any,
        data_room: config.data_room as any,
        ai: config.ai as any,
        lp_management: config.lp_management as any,
        team_collaboration: config.team_collaboration as any,
        compliance: config.compliance as any,
        cqdb: config.cqdb as any,
        seats: config.seats as any,
      });
    }
    setIsEditingPlanConfig(false);
  };

  const handleBalanceUsageSubmit = async (data: UpdateBalanceUsageRequest) => {
    if (!firm?.id) return;

    setBalanceUsageSuccessMessage(null);
    try {
      await updateBalanceUsage.mutateAsync({
        firmId: firm.id,
        data,
      });
      setBalanceUsageSuccessMessage('Balance usage updated successfully!');
      setIsEditingBalanceUsage(false);
      setTimeout(() => setBalanceUsageSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update balance usage:', error);
    }
  };

  const handleBalanceUsageCancel = () => {
    setIsEditingBalanceUsage(false);
  };

  const updateNestedValue = (path: string[], value: any) => {
    setPlanConfigFormData((prev) => {
      const newData = { ...prev };
      let current: any = newData;

      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }

      if (value === '' || value === null || value === undefined) {
        delete current[path[path.length - 1]];
      } else {
        current[path[path.length - 1]] = value;
      }

      return newData;
    });
  };

  const getNestedValue = (path: string[]): any => {
    let current: any = planConfigFormData;
    for (const key of path) {
      if (current && typeof current === 'object') {
        current = current[key];
      } else {
        return undefined;
      }
    }
    return current;
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[75vw] max-w-[75vw] overflow-y-auto p-0 sm:w-[75vw]">
        <div className="flex h-full flex-col">
          <SheetHeader className="border-b border-stroke-primary px-6 py-4">
            <div className="flex items-start justify-between pr-8">
              <div className="flex-1">
                <SheetTitle className="mb-2 flex items-center text-xl">
                  <Building2 className="mr-3 h-5 w-5 text-text-secondary" />
                  {firm.name}
                </SheetTitle>
                <SheetDescription className="text-base">Detailed information about {firm.name}</SheetDescription>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Badge variant={getPlanBadgeVariant(firm.plan)}>{firm.plan}</Badge>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="plan-config">Plan Config</TabsTrigger>
                <TabsTrigger value="balance-usage">Balance Usage</TabsTrigger>
              </TabsList>

              {/* Plan Config Tab */}
              <TabsContent value="plan-config" className="space-y-6">
                {isLoadingPlanConfig ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner size="lg" text="Loading plan configuration..." />
                  </div>
                ) : planConfigError ? (
                  <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-600">
                      {planConfigError.message || 'Failed to load plan configuration'}
                    </p>
                  </div>
                ) : planConfigData ? (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-text-primary">Plan Configuration</h3>
                      <Button
                        variant={isEditingPlanConfig ? 'outline' : 'default'}
                        size="sm"
                        onClick={() => (isEditingPlanConfig ? handlePlanConfigCancel() : setIsEditingPlanConfig(true))}
                      >
                        {isEditingPlanConfig ? 'Cancel' : 'Edit Plan Config'}
                      </Button>
                    </div>

                    {isEditingPlanConfig ? (
                      <PlanConfigForm
                        formData={planConfigFormData}
                        isPending={updatePlanConfig.isPending}
                        isError={updatePlanConfig.isError}
                        error={updatePlanConfig.error}
                        successMessage={planConfigSuccessMessage}
                        onUpdateNestedValue={updateNestedValue}
                        getNestedValue={getNestedValue}
                        onSubmit={handlePlanConfigSubmit}
                        onCancel={handlePlanConfigCancel}
                      />
                    ) : (
                      <PlanConfigDisplay planConfig={planConfigData} />
                    )}
                  </>
                ) : null}
              </TabsContent>

              {/* Balance Usage Tab */}
              <TabsContent value="balance-usage" className="space-y-6">
                {isLoadingBalanceUsage ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner size="lg" text="Loading balance usage..." />
                  </div>
                ) : balanceUsageError ? (
                  <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-600">
                      {balanceUsageError.message || 'Failed to load balance usage'}
                    </p>
                  </div>
                ) : balanceUsageData ? (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-text-primary">Balance & Usage</h3>
                      <Button
                        variant={isEditingBalanceUsage ? 'outline' : 'default'}
                        size="sm"
                        onClick={() =>
                          isEditingBalanceUsage ? handleBalanceUsageCancel() : setIsEditingBalanceUsage(true)
                        }
                      >
                        {isEditingBalanceUsage ? 'Cancel' : 'Adjust Credits'}
                      </Button>
                    </div>

                    {isEditingBalanceUsage ? (
                      <BalanceUsageForm
                        onSubmit={handleBalanceUsageSubmit}
                        onCancel={handleBalanceUsageCancel}
                        isPending={updateBalanceUsage.isPending}
                        isError={updateBalanceUsage.isError}
                        error={updateBalanceUsage.error}
                        successMessage={balanceUsageSuccessMessage}
                      />
                    ) : (
                      <>
                        <div className="space-y-4">
                          <div className="rounded-lg bg-surface-secondary p-4">
                            <h4 className="mb-3 text-sm font-semibold text-text-primary">Email Reveal Usage</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-text-secondary">Period:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.emailRevealUsage.period}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Limit:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.emailRevealUsage.limit}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Used:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.emailRevealUsage.used}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Remaining:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.emailRevealUsage.remaining}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-lg bg-surface-secondary p-4">
                            <h4 className="mb-3 text-sm font-semibold text-text-primary">AI Query Usage</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-text-secondary">Period:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.aiQueryUsage.period}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Limit:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.aiQueryUsage.limit}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Used:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.aiQueryUsage.used}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Remaining:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.aiQueryUsage.remaining}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-lg bg-surface-secondary p-4">
                            <h4 className="mb-3 text-sm font-semibold text-text-primary">NDA Signature Usage</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-text-secondary">Period:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.ndaSignatureUsage.period}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Limit:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.ndaSignatureUsage.limit}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Used:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.ndaSignatureUsage.used}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary">Remaining:</span>
                                <span className="ml-2 font-medium text-text-primary">
                                  {balanceUsageData.ndaSignatureUsage.remaining}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : null}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
